import ReactMarkdown, { type Components, defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { remarkCollapsible } from './collapsibleEncoding';
import { parseMentionHref } from './mentionEncoding';
import { remarkTaskRefs, TASK_REF_HREF_PREFIX } from './taskRefEncoding';

/**
 * Sanitization schema = rehype-sanitize default + `mention` href protocol.
 *
 * The default protocols allowlist (`http`, `https`, `irc`, `ircs`, `mailto`,
 * `xmpp`) drops `mention:user:123` hrefs. We extend it minimally so the
 * D-04 mention encoding survives sanitization and the `components.a`
 * renderer can detect it. `javascript:` remains unlisted — Test 7 still
 * asserts `javascript:` hrefs are stripped.
 */
export const sanitizeSchema = {
  ...defaultSchema,
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), 'mention'],
  },
};

/**
 * react-markdown@9 runs its own pre-sanitization URL allowlist
 * (`defaultUrlTransform`) BEFORE rehype-sanitize sees the tree, and that
 * allowlist hardcodes only `https?`, `ircs?`, `mailto`, `xmpp`. Any
 * `mention:` href would be rewritten to `''` and the chip detector would
 * fail. We pass through `mention:` URLs unchanged and delegate every other
 * scheme to the library default — so `javascript:` stays scrubbed.
 *
 * Signature matches the `UrlTransform` callback react-markdown invokes:
 * `(value, key, node) => string`. The exported `defaultUrlTransform` type
 * only declares `(value)`, but the implementation accepts and ignores the
 * extra args, so a single-arg call is safe.
 */
export function urlTransform(value: string, key: string): string {
  if (key === 'href' && /^mention:(user|external):\d+$/.test(value)) {
    return value;
  }
  return defaultUrlTransform(value);
}

export interface MarkdownBodyProps {
  body: string;
  /** Extra wrapper class names. */
  className?: string;
}

/**
 * Phase 25 D-01 / D-02 — sanitized markdown renderer used for both task
 * descriptions and comment bodies. Single source of truth for the
 * sanitization schema so the two surfaces cannot drift.
 *
 * Enabled (GFM subset): paragraphs, bold/italic, inline + fenced code,
 * ordered/unordered lists, links (auto-linked), blockquotes, ~~strike~~,
 * tables.
 *
 * Disabled: raw HTML (rehype-sanitize default), markdown image syntax (D-02 —
 * attachments handle images via signed URLs), JS-protocol links (rehype-
 * sanitize default).
 *
 * Mentions: a custom <a> renderer detects the `mention:` URL prefix from
 * D-04 and renders a styled chip (D-05). All other links render with
 * target=_blank + rel=noopener noreferrer.
 *
 * Task refs (260819-kq3): `remarkTaskRefs` turns plain-text `#MKS-138` tokens
 * into `/tasks/MKS-138` links; the <a> renderer styles them as clickable
 * chips navigating same-tab to the MKS-144 full-page ticket view.
 *
 * Collapsible blocks (MKS-183): `remarkCollapsible` turns Linear's
 * `+++ Title … +++` blocks into <details>/<summary>. It runs before
 * `remarkTaskRefs` so chips and mentions inside a block still linkify.
 *
 * Long URLs (MKS-194): an auto-linked URL wraps inside the comment bubble
 * instead of pushing it sideways.
 */
export function MarkdownBody({ body, className }: MarkdownBodyProps) {
  if (!body) return null;

  const components: Components = {
    a: ({ href, children, ...rest }) => {
      const mention = parseMentionHref(href);
      if (mention) {
        const label = mention.kind === 'user' ? 'Internal user' : 'External user';
        return (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-700 no-underline text-sm font-medium"
            title={label}
            data-mention-kind={mention.kind}
            data-mention-id={mention.id}
          >
            {children}
          </span>
        );
      }
      if (href?.startsWith(TASK_REF_HREF_PREFIX)) {
        // 260819-kq3 — task-ref chip. Same-tab navigation (ordinary link
        // semantics; cmd-click still opens a new tab) to the shareable
        // full-page ticket view.
        return (
          <a
            href={href}
            className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-700 hover:bg-primary-100 no-underline text-sm font-medium"
            title="Open referenced task"
            data-task-ref
          >
            {children}
          </a>
        );
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 underline break-words"
          {...rest}
        >
          {children}
        </a>
      );
    },
    // D-02 — markdown images render to nothing; attachments handle images.
    img: () => null,
    // MKS-143 — Tailwind Preflight resets heading font-size/weight, so
    // without explicit classes h1–h6 render identically to body text.
    // Sized against the text-sm body: three clearly distinct top levels,
    // h4–h6 stay at body size but keep weight distinction.
    h1: ({ children }) => <h1 className="text-xl font-bold mt-4 mb-1.5">{children}</h1>,
    h2: ({ children }) => <h2 className="text-lg font-semibold mt-3 mb-1">{children}</h2>,
    h3: ({ children }) => <h3 className="text-base font-semibold mt-3 mb-1">{children}</h3>,
    h4: ({ children }) => <h4 className="text-sm font-semibold mt-2 mb-0.5">{children}</h4>,
    h5: ({ children }) => <h5 className="text-sm font-medium mt-2 mb-0.5">{children}</h5>,
    h6: ({ children }) => (
      <h6 className="text-sm font-medium text-gray-600 mt-2 mb-0.5">{children}</h6>
    ),
    // MKS-183 — `+++ Title … +++` collapsible blocks. The sanitizer keeps the
    // `details`/`summary` tags but drops `className` from the plugin's
    // hProperties, so all styling lives here. Closed by default: the point is
    // that the first screen of a long report stays readable.
    details: ({ children }) => (
      <details className="my-2 rounded border border-gray-200 bg-gray-50 px-3 py-2">
        {children}
      </details>
    ),
    summary: ({ children }) => (
      <summary className="cursor-pointer font-medium text-gray-700 marker:text-gray-400">
        {children}
      </summary>
    ),
  };

  return (
    <div className={className ?? 'text-sm text-gray-800 leading-relaxed space-y-2'}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkCollapsible, remarkTaskRefs]}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
        urlTransform={urlTransform}
        components={components}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}
